import { Trans } from "@lingui/react/macro";
import { useState } from "react";
import { useNavigate } from "react-router";
import {
  InstrumentWithFilters,
  InstrumentWithFiltersAndTranslations,
} from "../../api/instrumentApi.ts";
import {
  useDeleteLocationMutation,
  useLocationsQuery,
} from "../../api/locationApi.ts";
import { Translations } from "../../api/util/sharedModels.ts";
import ActionButton from "../../components/ActionButton.tsx";
import Container from "../../components/Container.tsx";
import ContainerHeader from "../../components/ContainerHeader.tsx";
import ErrorState from "../../components/ErrorState.tsx";
import { useLanguageStore } from "../../stores/languageStore.ts";

export default function EditLocations() {
  const {
    data: locations,
    refetch: refetchAllLocations,
    status,
  } = useLocationsQuery();
  const { mutate: deleteLocation, isPending: deletionPending } =
    useDeleteLocationMutation();
  const [deletionId, setDeletionId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { language } = useLanguageStore();

  function handleEditLocation(locationId: string) {
    navigate(`/mystudio/locations/${locationId}`);
  }

  const getTranslation = (
    instrument: InstrumentWithFilters | InstrumentWithFiltersAndTranslations,
    language: string,
  ) => {
    return instrument.translations[language as keyof Translations];
  };

  return (
    <>
      <Container>
        {/* Existing Locations Display */}
        <ContainerHeader className={"flex"}>
          <Trans>Your teaching locations</Trans>
          <ActionButton
            type={"button"}
            onClick={() => handleEditLocation("new")}
            className={"ml-auto"}
          >
            <Trans>Create Location</Trans>
          </ActionButton>
        </ContainerHeader>

        <div className={"mt-4"}>
          {status == "error" && <ErrorState />}
          {locations && locations.length === 0 ? (
            <p className="text-center text-lg text-gray-600">
              <Trans>Add some locations where you are teaching!</Trans>
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3 xl:grid-cols-4">
              {locations &&
                status !== "error" &&
                locations.map((location) => (
                  <Container key={location.id} className="overflow-hidden">
                    <div className="flex h-full flex-col p-2">
                      <div className="flex-grow">
                        <p className="mb-4 text-lg font-semibold">
                          {location.name}
                        </p>
                        {location.streetName && (
                          <p className="mb-1 text-sm text-gray-600">
                            <span className="font-medium">
                              <Trans>Address</Trans>:
                            </span>{" "}
                            {location.streetName} {location.houseNumber}
                          </p>
                        )}
                        {location.city && (
                          <p className="mb-1 text-sm text-gray-600">
                            <span className="font-medium">
                              <Trans>City</Trans>:
                            </span>{" "}
                            {location.city}
                          </p>
                        )}
                        {location.postalCode && (
                          <p className="mb-1 text-sm text-gray-600">
                            <span className="font-medium">
                              <Trans>Postal Code</Trans>:
                            </span>{" "}
                            {location.postalCode}
                          </p>
                        )}
                        {location.countryCode && (
                          <p className="mb-1 text-sm text-gray-600">
                            <span className="font-medium">
                              <Trans>Country</Trans>:
                            </span>{" "}
                            {location.countryCode}
                          </p>
                        )}
                        {location.timeZone && (
                          <p className="mb-1 text-sm text-gray-600">
                            <span className="font-medium">
                              <Trans>Timezone</Trans>:
                            </span>{" "}
                            {location.timeZone}
                          </p>
                        )}
                        {location.instruments.length > 0 && (
                          <p className="mb-1 text-sm text-gray-600">
                            <span className="font-medium">
                              <Trans>Instruments</Trans>:
                            </span>{" "}
                            {location.instruments
                              .map((instrument) =>
                                getTranslation(instrument, language),
                              )
                              .join(", ")}
                          </p>
                        )}
                        {location.instruments.length == 0 && (
                          <p className="mb-1 text-sm text-gray-600">
                            <span className="font-medium text-red-500">
                              <Trans>Instruments missing!</Trans>
                            </span>
                          </p>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <ActionButton
                          disabled={deletionId === location.id}
                          isLoading={false}
                          onClick={() => handleEditLocation(location.id)}
                          className="mt-2 flex-1"
                          variant="secondary"
                          icon={"Pencil"}
                        >
                          <Trans>Edit</Trans>
                        </ActionButton>
                        <ActionButton
                          disabled={deletionPending}
                          isLoading={deletionId === location.id}
                          onClick={() => {
                            setDeletionId(location.id);
                            deleteLocation(location.id, {
                              onSettled: () =>
                                refetchAllLocations()
                                  .catch(console.error)
                                  .finally(() => setDeletionId(null)),
                            });
                          }}
                          className="mt-2 flex-1"
                          variant="destructive"
                          icon={"Trash"}
                        >
                          <Trans>Delete</Trans>
                        </ActionButton>
                      </div>
                    </div>
                  </Container>
                ))}
            </div>
          )}
        </div>
      </Container>
    </>
  );
}
